import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  checkEmailFormat,
  formatEuro,
  LEGAL_UPDATED,
  PASSWORD_MIN_LENGTH,
  PRIVATE_FREE_STORAGE_MB,
  PRIVATE_PLUS_BOOKABLE,
  PRIVATE_PLUS_PRICE_CENTS,
  PRIVATE_PLUS_STORAGE_MB,
  PROVIDER,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { Field, Notice } from "../components/ui";
import { useStats } from "../hooks";

const gb = (mb: number) => `${mb / 1024} GB`;
const ADDRESS = `${PROVIDER.street}, ${PROVIDER.zip} ${PROVIDER.city}`;

export function PrivatPreise() {
  return (
    <section data-page="privat-preise" className="wrap chapter">
      <div className="proof-head">
        <span className="eyebrow">Preise für Zuhause</span>
        <h1>Zum Start kostenlos. Mehr Platz für wenig Geld.</h1>
        <p className="lead">Zwei Möglichkeiten, ohne Vertragsfalle. Alle Preise sind Endpreise inklusive Umsatzsteuer.</p>
      </div>
      <div className="price-grid">
        <article className="card stack">
          <span className="tag go">Gratis</span>
          <h2>HandVero Zuhause</h2>
          <p className="price-big">0 €</p>
          <ul className="checks">
            <li>Hausakte mit Ordnern, Fotos, Rechnungen und Garantien</li>
            <li>Typenschild fotografieren, Gerätekarte als PDF</li>
            <li>Termine und Erinnerungen für Wartung und Prüfungen</li>
            <li>
              <b>{gb(PRIVATE_FREE_STORAGE_MB)}</b> Speicher pro Konto
            </li>
            <li>Vero erklärt, ohne Verkaufsdruck</li>
          </ul>
          <p className="small muted">
            Hinweis: Die kostenlose Version kann später Werbung enthalten. Wir verwenden dabei kein Tracking und geben keine Daten an Werbefirmen weiter. Sie stimmen
            dem bei der Anmeldung ausdrücklich zu.
          </p>
          <Link className="btn btn-primary" to="/privat/registrieren">
            Kostenlos anmelden
          </Link>
        </article>
        <article className="card stack">
          <span className="tag">Plus</span>
          <h2>HandVero Zuhause Plus</h2>
          <p className="price-big">
            {formatEuro(PRIVATE_PLUS_PRICE_CENTS)} <small>pro Monat</small>
          </p>
          <ul className="checks">
            <li>Alles aus der kostenlosen Version</li>
            <li>
              <b>{gb(PRIVATE_PLUS_STORAGE_MB)}</b> Speicher pro Konto
            </li>
            <li>Keine Werbung</li>
            <li>Monatlich kündbar</li>
          </ul>
          {PRIVATE_PLUS_BOOKABLE ? (
            <Link className="btn btn-primary" to="/privat/registrieren">
              Plus wählen
            </Link>
          ) : (
            <p className="small muted">
              <b>Demnächst buchbar.</b> Wenn Sie schon jetzt mehr als {gb(PRIVATE_FREE_STORAGE_MB)} brauchen, schreiben Sie uns kurz über das{" "}
              <Link to="/kontakt">Kontaktformular</Link>.
            </p>
          )}
        </article>
      </div>
      <p className="note-small">
        Sie arbeiten selbstständig oder haben einen Betrieb? Dann sind die <Link to="/preise">Betriebs-Preise</Link> richtig für Sie.
      </p>
    </section>
  );
}

export function PrivatRegistrieren() {
  const navigate = useNavigate();
  const stats = useStats();
  const openedAt = useRef(Date.now());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const closed = stats ? !stats.privateSignupOpen : false;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    setError("");
    const get = (n: string) => (f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "";
    const checked = (n: string) => (f.elements.namedItem(n) as HTMLInputElement | null)?.checked ?? false;
    const email = get("email");
    const pre = checkEmailFormat(email);
    if (!pre.ok) return setError(pre.message);
    if (get("password").length < PASSWORD_MIN_LENGTH) return setError(`Das Passwort braucht mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`);
    if (!checked("acceptTerms")) return setError("Bitte stimmen Sie den Nutzungsbedingungen zu und bestätigen Sie, dass Sie die Datenschutzerklärung gelesen haben.");
    if (!checked("acceptAds")) return setError("Bitte bestätigen Sie den Hinweis zur Werbung. Ohne ihn können wir die kostenlose Version nicht anbieten.");
    setBusy(true);
    try {
      const res = await api.post<{ email: string }>("/api/auth/register-private", {
        name: get("name"),
        email,
        password: get("password"),
        acceptTerms: true,
        acceptAds: true,
        marketingMail: checked("marketingMail"),
        website: get("website"),
        formMs: Date.now() - openedAt.current,
      });
      navigate("/registrierung-gesendet", { state: { email: res.email } });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-page="privat-registrieren">
      <div className="wrap reg-layout">
        <div className="stack">
          <span className="eyebrow">HandVero Zuhause</span>
          <h1>Kostenlos anmelden</h1>
          <p className="small muted">Für Privatpersonen. Für Betriebe gibt es die <Link to="/registrieren">Firmenanmeldung</Link>.</p>
          {closed && (
            <Notice kind="info">
              <b>Die Anmeldung für Privatpersonen öffnet bald.</b> Wir testen noch. Sobald es losgeht, steht es hier.
            </Notice>
          )}
          <form className="form" onSubmit={submit} noValidate>
            <Field id="name" label="Ihr Name" autoComplete="name" required disabled={closed} />
            <Field
              id="email"
              label="E-Mail"
              type="email"
              autoComplete="email"
              required
              disabled={closed}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v.trim()) return setEmailHint("");
                const r = checkEmailFormat(v);
                setEmailHint(r.ok ? "" : r.message);
              }}
              hint={emailHint || undefined}
            />
            <Field
              id="password"
              label="Passwort"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              disabled={closed}
              hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
            />
            <div className="hp" aria-hidden="true">
              <label htmlFor="website">Webseite</label>
              <input id="website" name="website" tabIndex={-1} autoComplete="off" />
            </div>
            <label className="check-row">
              <input type="checkbox" name="acceptTerms" disabled={closed} />
              <span>
                Ich stimme den <Link to="/privat/agb">Nutzungsbedingungen für Privatpersonen</Link> zu und habe die{" "}
                <Link to="/privat/agb#datenschutz">Datenschutzhinweise</Link> gelesen.
              </span>
            </label>
            <label className="check-row">
              <input type="checkbox" name="acceptAds" disabled={closed} />
              <span>
                Ich weiß, dass die kostenlose Version später <b>Werbung</b> enthalten kann (ohne Tracking, ohne Weitergabe meiner Daten).
              </span>
            </label>
            <label className="check-row">
              <input type="checkbox" name="marketingMail" disabled={closed} />
              <span>Freiwillig: Ich möchte gelegentlich Tipps und Neuigkeiten per E-Mail. Abbestellen geht jederzeit mit einem Klick.</span>
            </label>
            <Notice kind="bad">{error}</Notice>
            <button className="btn btn-primary" type="submit" disabled={busy || closed}>
              {busy ? "Wird angelegt …" : "Konto anlegen"}
            </button>
            <p className="small muted">
              Schon angemeldet? <Link to="/anmelden">Anmelden</Link>
            </p>
          </form>
        </div>
        <div className="stack">
          <div className="card">
            <h3>Das bekommen Sie</h3>
            <ul className="checks">
              <li>{gb(PRIVATE_FREE_STORAGE_MB)} Speicher für Ihre Hausakte</li>
              <li>Typenschild-Foto wird zur Gerätekarte</li>
              <li>Keine Kosten, keine Zahlungsdaten nötig</li>
              <li>Ihre Daten bleiben in Deutschland</li>
            </ul>
          </div>
          <div className="card">
            <p className="small">
              Mehr Platz und keine Werbung: <Link to="/privat/preise">Plus für {formatEuro(PRIVATE_PLUS_PRICE_CENTS)} im Monat</Link>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PrivatRecht() {
  return (
    <section data-page="privat-agb" className="wrap chapter legal">
      <div className="proof-head">
        <span className="eyebrow">Rechtliches für Privatpersonen</span>
        <h1>Nutzungsbedingungen HandVero Zuhause</h1>
        <p className="lead">Stand: {LEGAL_UPDATED}. Einfach geschrieben, damit Sie wissen, woran Sie sind.</p>
      </div>

      <div className="stack legal-part" id="agb">
        <h2>1. Wer wir sind und was gilt</h2>
        <p>
          Anbieter ist {PROVIDER.owner}, {ADDRESS} („wir“). Diese Bedingungen gelten für „HandVero Zuhause“, das Angebot für Privatpersonen (Verbraucher). Für Betriebe gelten
          die <Link to="/rechtliches#agb">Bedingungen für Unternehmer</Link>.
        </p>
        <h2>2. Was Sie bekommen</h2>
        <p>
          Eine Hausakte im Internet: Ordner, Fotos, Dokumente, Gerätekarten und Termine. Die kostenlose Version bietet {gb(PRIVATE_FREE_STORAGE_MB)} Speicher pro Konto. Ist der Speicher voll,
          können Sie keine neuen Dateien mehr hochladen, bis Sie etwas löschen oder auf Plus wechseln. Vorhandenes bleibt erhalten und lesbar.
        </p>
        <h2>3. Kostenlose Version und Werbung</h2>
        <p>
          Die kostenlose Version kostet nichts. Wir behalten uns vor, darin später Werbung anzuzeigen. Wir setzen dafür kein Tracking ein und geben Ihre Daten nicht an Werbefirmen weiter.
          Darauf weisen wir bei der Anmeldung hin und holen Ihre ausdrückliche Zustimmung ein. Wir kündigen Werbung vorher an. Sie können jederzeit auf Plus wechseln (sobald buchbar) oder Ihr Konto löschen.
        </p>
        <h2>4. Plus</h2>
        <p>
          Plus kostet {formatEuro(PRIVATE_PLUS_PRICE_CENTS)} pro Monat inklusive Umsatzsteuer, bietet {gb(PRIVATE_PLUS_STORAGE_MB)} Speicher und ist werbefrei. Plus ist monatlich kündbar,
          die Kündigung wirkt zum Ende des bezahlten Monats. Plus ist derzeit noch nicht buchbar. Vor der Buchung zeigen wir Ihnen Preis und Bedingungen erneut.
        </p>
        <h2>5. Widerruf</h2>
        <p>
          Für kostenpflichtige Verträge (Plus) haben Sie als Verbraucher ein gesetzliches Widerrufsrecht von 14 Tagen. Die ausführliche Widerrufsbelehrung und ein Muster-Widerrufsformular
          erhalten Sie, bevor Sie Plus buchen können. Die kostenlose Version ist ein unentgeltlicher Vertrag, den Sie jederzeit beenden können.
        </p>
        <h2>6. Ihre Pflichten</h2>
        <p>
          Halten Sie Ihr Passwort geheim. Laden Sie nur Inhalte hoch, die Sie hochladen dürfen, und nichts Rechtswidriges. Bitte sichern Sie wichtige Unterlagen zusätzlich selbst, zum Beispiel mit
          dem Daten-Export unter „Ihre Daten“.
        </p>
        <h2>7. Haftung</h2>
        <p>
          Wir haften unbeschränkt bei Vorsatz, grober Fahrlässigkeit, Verletzung von Leben, Körper und Gesundheit und nach dem Produkthaftungsgesetz. Bei leicht fahrlässiger Verletzung wesentlicher
          Vertragspflichten ist die Haftung auf den vorhersehbaren, typischen Schaden begrenzt. Die Erklärtexte und Gerätekarten sind allgemeine Information und ersetzen keine Fachkraft und keine
          Herstellerangaben. Die per Texterkennung gelesenen Daten können Fehler enthalten, bitte prüfen Sie sie.
        </p>
        <h2>8. Kündigung und Löschung</h2>
        <p>
          Sie können Ihr Konto jederzeit löschen. Vorher können Sie alle Ihre Daten als ZIP herunterladen. Wir können das Konto mit angemessener Frist kündigen, bei Missbrauch auch sofort.
        </p>
        <h2>9. Schlussbestimmungen</h2>
        <p>
          Es gilt deutsches Recht; zwingende Verbraucherschutzvorschriften Ihres Wohnsitzstaates bleiben unberührt. Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen. Die Plattform der EU-Kommission zur Online-Streitbeilegung wurde eingestellt.
        </p>
      </div>

      <div className="stack legal-part" id="datenschutz">
        <h2>Datenschutz für Privatpersonen</h2>
        <p>
          Wir speichern Name, E-Mail-Adresse und die Inhalte, die Sie selbst ablegen, um die Hausakte bereitzustellen (Art. 6 Abs. 1 lit. b DSGVO). Die Daten liegen auf Servern in Deutschland. Die Texterkennung
          beim Typenschild läuft auf Ihrem Gerät, das Foto wird dafür nicht an Dritte geschickt. Wir nutzen kein Tracking und keine Werbenetzwerke.
        </p>
        <p>
          Teilen Sie einen Ordner mit einem Betrieb („Mein Handwerker“), schicken wir einen Link an die von Ihnen genannte E-Mail-Adresse. Der Betrieb kann die Dateien dieses Ordners nur ansehen und
          herunterladen. Der Link läuft ab und lässt sich jederzeit zurückziehen. Bitte teilen Sie nur, was der Betrieb sehen soll.
        </p>
        <p>
          Schickt Ihnen ein Betrieb eine Übergabe-Akte, sehen Sie die Dateien über einen Link. Erst wenn Sie sie in Ihr Konto übernehmen, kopieren wir sie in Ihre Hausakte; sie zählen dann zu Ihrem Speicher.
          Fotos, die ohne Internet aufgenommen werden, warten zunächst nur auf Ihrem Gerät und werden hochgeladen, sobald wieder Verbindung besteht.
        </p>
        <p>
          Die Zustimmung zu Werbung in der kostenlosen Version bestätigen Sie bei der Anmeldung. E-Mails mit Tipps und Neuigkeiten schicken wir nur, wenn Sie das freiwillig angekreuzt haben (Art. 6 Abs. 1 lit. a DSGVO);
          Sie können das jederzeit widerrufen. Ihre Rechte auf Auskunft, Berichtigung, Löschung und Datenübertragbarkeit erfüllen wir über „Ihre Daten“ oder per E-Mail an{" "}
          <a href={`mailto:${PROVIDER.email}`}>{PROVIDER.email}</a>. Mehr in der <Link to="/rechtliches#datenschutz">allgemeinen Datenschutzerklärung</Link>.
        </p>
      </div>
      <p className="note-small">Diese Texte sind noch nicht anwaltlich geprüft und werden vor dem Start der Anmeldung geprüft.</p>
    </section>
  );
}
